import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { LoadingBlockFullPage } from '@plato/ui-legacy/components/loading/loading-block-full-page';
import { TitleWithCopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { TabsHeader } from '@plato/ui-legacy/components/ui/tabs-header';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { Container } from '@plato/ui/containers/container';
import { DialogBody, DialogHeader } from '@plato/ui/containers/dialog';
import { cn } from '@plato/ui/util/classes';
import { ChevronLeftIcon, XIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';
import { QuoteOrderActionDropdownMenu } from '../components/quote-order-action-dropdown-menu.tsx';
import { QuoteOrderAttachmentsTab } from '../components/quote-order-attachments-tab.tsx';
import { QuoteOrderDetailTab } from '../components/quote-order-detail-tab.tsx';
import { QuoteOrderNotFound } from '../components/quote-order-not-found.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';

const PageTabs = {
    DETAILS: 'details',
    ATTACHMENTS: 'attachments',
} as const;

type PageTabs = (typeof PageTabs)[keyof typeof PageTabs];

type Props = {
    className?: string;
    onBack?: () => void;
    onClose?: () => void;
    type: SrSalesOrderType;
    transactionId: string;
};

export function QuoteOrderDetailPage({ onBack, onClose, className, transactionId, type }: Props) {
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState<PageTabs>(PageTabs.DETAILS);

    const { canViewSalesOrderDocuments } = useAppConfig();

    const { data, isLoading } = useDetailQuoteOrder({ transactionId });
    const sendEvent = useAnalytics();
    const customerId = data?.header.customer_id;

    useEffect(() => {
        if (customerId) {
            sendEvent('quote_order_detail_viewed', {
                customer_id: customerId,
                quote_order_type: type,
                transaction_id: transactionId,
            });
        }
    }, [customerId, sendEvent, transactionId, type]);

    if (isLoading) {
        return <LoadingBlockFullPage className="min-h-96" />;
    }

    if (!data) {
        return <QuoteOrderNotFound onBack={onBack} onClose={onClose} />;
    }

    const { header } = data;

    const title = `${
        type === SrSalesOrderType.ORDER
            ? t('order', { ns: 'transaction-details' })
            : t('quote', { ns: 'transaction-details' })
    } ${header.display_identifier}`;

    return (
        <Container
            level={1}
            className={cn('flex h-full min-h-0 w-full grow flex-col bg-neutral-50', className)}
        >
            {/* The tabs' own rule is the band's bottom edge, so the header suppresses its own. */}
            <DialogHeader className="flex-col items-stretch border-b-0 bg-white pb-0">
                <div className="flex items-center gap-default">
                    {onBack && (
                        <IconButton label={t('back', { ns: 'common' })} onClick={onBack}>
                            <ChevronLeftIcon />
                        </IconButton>
                    )}
                    <div className="flex min-w-0 grow flex-row items-center gap-2">
                        <h1 className="flex min-w-0 flex-row items-center text-heading-3">
                            {header.display_identifier ? (
                                <TitleWithCopyButton text={title} textToCopy={header.display_identifier} />
                            ) : (
                                title
                            )}
                        </h1>
                        <OrderStatusBadge status={header.status} type={header.type} className="ml-0.5" />
                    </div>
                    <QuoteOrderActionDropdownMenu
                        customerId={header.customer_id}
                        type={type}
                        transactionId={transactionId}
                        onDelete={onClose ?? onBack}
                    />
                    {onClose && (
                        <IconButton label={t('form__close', { ns: 'common' })} onClick={onClose}>
                            <XIcon />
                        </IconButton>
                    )}
                </div>
                <TabsHeader<PageTabs>
                    selectedTabKey={selectedTab}
                    onTabChange={(value) => {
                        setSelectedTab(value);
                    }}
                    tabs={[PageTabs.DETAILS, ...(canViewSalesOrderDocuments ? [PageTabs.ATTACHMENTS] : [])]}
                    getTabLabel={(tab) => t(`tabs__${tab}`, { ns: 'transaction-details' })}
                />
            </DialogHeader>
            <DialogBody className="flex flex-col gap-container-cozy py-container-cozy">
                {selectedTab === PageTabs.DETAILS && (
                    <QuoteOrderDetailTab data={data} type={type} isLoading={isLoading} />
                )}
                {selectedTab === PageTabs.ATTACHMENTS && (
                    <QuoteOrderAttachmentsTab transactionId={transactionId} />
                )}
            </DialogBody>
        </Container>
    );
}
